import {
  Currency,
  KitStatus,
  PDF_QC,
  ReportType,
  SixteenSMLQueueEnum,
  SpecialReportType,
  WorkflowTimeline,
} from '@/enums';
import {
  FileData,
  MetaTag,
  NestedKeyOf,
  TIMELINE_BASED_ON_WORKFLOW,
} from '@/interfaces';
import {
  PDF_PATH,
  PDF_PATH_V5,
  PDF_QC_ERROR_MESSAGE_V5,
} from '@/interfaces/report';
import {
  KitEntity,
  KitStatusHistoryEntity,
} from '@/pages/privatePages/kit/types';
import UploadService from '@/services/amili-v3-services/upload.service';
import { CustomColumnType } from '@amili-v3/amili-v3-design';
import { notification } from 'antd';
import { ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import i18next from 'i18next';
import JSZip from 'jszip';
import { isEmpty } from 'lodash';
import _chunk from 'lodash/chunk';
import _flatMap from 'lodash/flatMap';
import _get from 'lodash/get';
import _uniq from 'lodash/uniq';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { saveBlob } from './exportFuncs';
import { getStatusHistory } from '@/pages/authenPages/Kit/utils';

export const rowKey = (record: any) => record?.id;

export const onError = (error: any) => {
  notification.error({
    message:
      error.response?.data?.message ||
      error.response?.data?.error ||
      error?.message,
  });
};

export const useToggle = (
  initValue: boolean = false,
): [boolean, () => void, Dispatch<SetStateAction<boolean>>] => {
  const [open, setOpen] = useState<boolean>(initValue);
  const toggleOpen = useCallback(() => {
    setOpen(s => !s);
  }, []);

  return [open, toggleOpen, setOpen];
};

export type IRenderColumnCondition = Array<{
  condition: boolean;
  indexArray: string[];
}>;

//filter columns.dataIndex not includes in renderColumnCondition.indexArray
export const filterColumn = (
  renderColumnCondition: IRenderColumnCondition,
  mainColumn: CustomColumnType<any>[],
) => {
  const dataIndexNeedFilterOutMap = renderColumnCondition.reduce(
    (res: Record<string, boolean>, item) => {
      const { condition, indexArray } = item;
      if (!condition) return res;
      indexArray.forEach(key => {
        res[key] = true;
      });
      return res;
    },
    {},
  );

  return Object.keys(dataIndexNeedFilterOutMap).reduce(
    (result: ColumnType<any>[], key) => {
      return result.filter(c => {
        const dataIndex = Array.isArray(c.dataIndex)
          ? c.dataIndex.join('.')
          : c.dataIndex;
        return dataIndex !== key;
      });
    },
    mainColumn,
  ) as CustomColumnType<any>[];
};

export const renderCondition = (render: any) => {
  if (typeof render === 'undefined') {
    return '--';
  }
  if (typeof render === 'string') {
    const result = render.trim();
    return result ? result : '--';
  } else {
    return render;
  }
};

export const getCurrencySymbol = (locale: string, currency: string) =>
  (0)
    .toLocaleString(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\d/g, '')
    .trim();

export const formatNameProfileCol = (array: string[]) => {
  return array.filter(x => !!x).join(' ');
};
export const normalizeMultipleNames = (
  names: string[],
  translatePath?: string,
) =>
  names.reduce((res, name, currIndex) => {
    const translatedName = translatePath
      ? translatedTextFunc(name, translatePath)
      : name;
    if (currIndex !== names.length - 1) {
      res += translatedName + ', ';
    } else res += translatedName;
    return res;
  }, '');

export function formatAnalysisId(
  analysisId: string | null | undefined,
  removeHypens = false,
): string {
  if (!analysisId) return '--';
  if (!removeHypens) {
    if (analysisId.length === 16 && !['bm'].includes(analysisId.slice(0, 2))) {
      const chunks = _chunk(analysisId.split(''), 4);
      return chunks
        .map(item => item.join(''))
        .join('-')
        .toUpperCase();
    }
  }
  return analysisId?.toUpperCase();
}

export const downloadFile = async (
  file: string,
  name?: string,
  onError: () => void = () => {},
) => {
  const fileName = !name ? file.substr(file.lastIndexOf('/') + 1) : name;

  try {
    const response = await fetch(file);
    const blob = await response.blob();

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${fileName}`;
    link.click();
  } catch (_e) {
    onError();
  }

  // const element = document.createElement('a');
  // element.setAttribute('href', file);
  // element.setAttribute('download', fileName);

  // element.style.display = 'none';

  // document.body.appendChild(element);

  // element.click();
  // document.body.removeChild(element);
};
export interface IInputSheetData {
  csvData: unknown[];
  sheetName: string;
  columns?: string[];
}
export const exportMultiSheetExcel = async (
  input: IInputSheetData[],
  fileName: string,
  isXLS2003 = false,
) => {
  const fileType = isXLS2003
    ? 'application/vnd.ms-excel'
    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const fileExtension = isXLS2003 ? '.xls' : '.xlsx';
  const { utils, write } = await import('xlsx');

  let wb = utils.book_new();

  input.forEach(sheetData => {
    const { csvData, sheetName, columns } = sheetData;
    const ws = !!columns
      ? utils.json_to_sheet(csvData, { header: columns })
      : utils.json_to_sheet(csvData, { skipHeader: true });
    utils.book_append_sheet(wb, ws, sheetName);
  });

  const excelBuffer = write(wb, {
    bookType: isXLS2003 ? 'xls' : 'xlsx',
    type: 'array',
  });
  const data = new Blob([excelBuffer], { type: fileType });
  saveBlob(data, `${fileName}${fileExtension}`);
};

export const customRequest = async (options: any) => {
  let type = options.file.type;
  let nameImage = options.file.name;
  try {
    const res = await UploadService.getPreSignedUrlUpload(
      'products',
      nameImage,
      type,
    );
    const { data } = res;
    if (data) {
      await UploadService.putWithFormFileAsync(
        data.uploadUrl,
        options.file,
        type,
      );
      options.onSuccess({
        url: `${process.env.REACT_APP_S3_URL}/${data.fileKey}`,
      });
    }
  } catch (error) {
    options.onError(error);
  }
};

export const downloadFileZip = async (
  folderName: string,
  data: FileData[],
  onError: () => void = () => {},
) => {
  try {
    const zip = new JSZip();
    for (let i = 0; i < data.length; i++) {
      const response = await fetch(data[i].url);

      if (!response.ok) continue;

      const blob = await response.blob();

      zip.file(data[i].fileName, blob);
    }

    const zipData = await zip.generateAsync({
      type: 'blob',
      streamFiles: true,
    });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(zipData);
    link.download = `${folderName}.zip`;
    link.click();
  } catch (_e) {
    onError();
  }
};

type PdfQcErrorItem = {
  Name?: string;
  ErrorMessage?: string;
  ErrorDescription?: string;
};
export const getPdfQCErrorMessages = (
  pdfQc: PDF_QC,
  metaTags: MetaTag[],
): string[] => {
  if (pdfQc !== PDF_QC.FAILED) return [];
  const raw = metaTags.find(tag => tag?.key === PDF_QC_ERROR_MESSAGE_V5)?.value;
  if (!raw) return [];

  const parsed = JSON.parse(raw);
  const pdfQcErrorItems = Array.isArray(parsed)
    ? (parsed as PdfQcErrorItem[])
    : [];

  if (pdfQcErrorItems.length === 0) return [];
  return pdfQcErrorItems.reduce((acc: string[], error) => {
    const { ErrorMessage, ErrorDescription } = error;
    const message = (ErrorMessage ?? '').trim();
    const desc = (ErrorDescription ?? '').trim();

    if (!message && !desc) return acc;
    const part = message && desc ? `${message}: ${desc}` : message || desc;
    acc.push(part);
    return acc;
  }, []);
};

export const hasReportData = (metaTags: MetaTag[]): boolean => {
  return (metaTags || []).some(
    t => t.key === PDF_PATH || t.key === PDF_PATH_V5,
  );
};

//Function is used for Select Option Input when it show search by label option
export const filterOptionFunc = (inputValue: string, option?: any) => {
  return (
    `${option?.label} ${option.value}`
      .toLowerCase()
      .indexOf(inputValue.toLowerCase()) >= 0
  );
};
//<------------

export const objectKeys = <T extends object, P extends Array<keyof T>>(
  inputObject: T,
): P => {
  return Object.keys(inputObject) as P;
};

//For checking kits allow to Send to sequencing ----------->
export type ReportTypeAndSixteenSSelectedType = {
  reportTypesSelected: string[][];
  sixteenSQueuesSelected: SixteenSMLQueueEnum[];
};
export const reportTypeAndSixteenSDefault: ReportTypeAndSixteenSSelectedType = {
  reportTypesSelected: [],
  sixteenSQueuesSelected: [],
};

export const getReportProjectTypesAndSixteenSQueues = <T extends object>(
  selectedData: T[],
  keyPath: NestedKeyOf<Required<T>>,
): ReportTypeAndSixteenSSelectedType => {
  return selectedData.reduce((previousResult, selectedItem) => {
    const reportTypes = (_get(
      selectedItem,
      keyPath + '.externalOutputType',
      undefined,
    ) || _get(selectedItem, keyPath + '.workflow.report', [])) as string[];
    const sixteenS = (_get(selectedItem, keyPath + '.sixteenSQueue', '') ||
      '') as SixteenSMLQueueEnum;

    const reportTypesSelected = [
      ...previousResult.reportTypesSelected,
      reportTypes,
    ];
    const sixteenSQueuesSelected = [
      ...previousResult.sixteenSQueuesSelected,
      sixteenS,
    ];

    return { reportTypesSelected, sixteenSQueuesSelected };
  }, reportTypeAndSixteenSDefault);
};

export const checkAllowToSendSequencing = (
  reportList: string[][],
  sixteenSQueues: SixteenSMLQueueEnum[],
): boolean => {
  const selectLength = reportList.length;
  if (!selectLength) return false;
  if (selectLength === 1) return true;

  const initConditionInReportList: Record<
    'haveSpecialTypeInList' | 'haveEmptyInList',
    boolean
  > = {
    haveSpecialTypeInList: false,
    haveEmptyInList: false,
  };
  const { haveSpecialTypeInList, haveEmptyInList } = reportList.reduce(
    (
      accCondition: Record<
        'haveSpecialTypeInList' | 'haveEmptyInList',
        boolean
      >,
      report,
    ) => {
      if (!!report.length) {
        if (
          !report.some(
            type => type !== ReportType.ASV && type !== SpecialReportType.DADA2,
          )
        )
          accCondition.haveSpecialTypeInList = true;
      } else accCondition.haveEmptyInList = true;

      return accCondition;
    },
    initConditionInReportList,
  );

  if (haveEmptyInList && haveSpecialTypeInList) return false;

  const flatReportType = _uniq(_flatMap(reportList));

  const conditions = {
    hasOnlyDada2: true,
    hasOnlyASV: true,
    haveAllType: true,
  };
  flatReportType.forEach(typeItem => {
    if (haveSpecialTypeInList) {
      conditions.haveAllType = false;
      if (typeItem !== SpecialReportType.DADA2) {
        conditions.hasOnlyDada2 = false;
      }
      if (typeItem !== ReportType.ASV) {
        conditions.hasOnlyASV = false;
      }
    } else {
      conditions.hasOnlyDada2 = false;
      conditions.hasOnlyASV = false;
      if (!Object.keys(ReportType).includes(typeItem)) {
        conditions.haveAllType = false;
      }
    }
  });

  const isSameSixteenSQueue = _uniq(sixteenSQueues).length === 1;
  const allowSendForSequencing =
    (conditions.hasOnlyASV ||
      conditions.hasOnlyDada2 ||
      conditions.haveAllType) &&
    isSameSixteenSQueue;

  return allowSendForSequencing;
};

/**
 * Currently, we will validate from BE and throw needed error from BE to FE. This function could be out of date
 * @param kitsList
 */
// export const notAllowSendToSequencingMessage = (kitsList: KitPayload[]) => {
//   const reportTypesInFirstKit: string[] =
//     kitsList[0].project?.externalOutputType ||
//     kitsList[0].project?.workflow?.report ||
//     [];
//   const sixteenSInFirstKit = kitsList[0].project?.sixteenSQueue || '';

//   const errorSerials = kitsList.reduce((res: string[], currKit, currIndex) => {
//     const currReportTypes: string[] =
//       currKit.project?.externalOutputType ||
//       currKit.project?.workflow?.report ||
//       [];

//     const isSameSixteenS =
//       (currKit.project?.sixteenSQueue || '') === sixteenSInFirstKit;

//     const isSameTypesList =
//       !!currReportTypes.length &&
//       currReportTypes.length === reportTypesInFirstKit.length &&
//       !currReportTypes.some(
//         (_, typeIdx) =>
//           reportTypesInFirstKit[typeIdx] !== currReportTypes[typeIdx],
//       );

//     if (currIndex !== 0 && (!isSameTypesList || !isSameSixteenS))
//       return [...res, currKit.serial.toUpperCase()];
//     return res;
//   }, []);

//   errorNotification({
//     error: { errorCode: 'CONFLICT_REPORT_TYPE_OR_QUEUE' },
//     contentObj: { SERIAL: normalizeMultipleNames(errorSerials) },
//   });
// };
//<------------------

export const getToken = (
  tokenName: 'accessToken' | 'idToken' | 'refreshToken',
) => {
  const tokens = getTokens();

  return tokens[tokenName] ?? '';
};

export const getTokens = () => {
  const token: Record<string, string> = JSON.parse(
    localStorage.getItem('auth') ?? '{}',
  );

  return token;
};

export const getExpirationDays = () => {
  return Number(localStorage.getItem('expirationDays')) ?? 30;
};

export const setTokensIntoLocalhost = (tokens: Record<string, string>) => {
  localStorage.setItem('auth', JSON.stringify(tokens));
  window.dispatchEvent(new Event('storage'));
};

//not in use
// export const getUserPoolInfo = () => {
//   const userPoolInfo: AuthUserPoolInfo = JSON.parse(
//     localStorage.getItem('userPoolInfo') || '{}',
//   );

//   return userPoolInfo;
// };

export const clearAuthStorage = () => {
  // const keysToRemove = ['auth', 'i18nextLng', 'userPoolInfo'];
  const keysToRemove = ['auth'];
  keysToRemove.forEach(key => localStorage.removeItem(key));
  window.dispatchEvent(new Event('storage'));
};

const kitStatusCounterAllowedDays = {
  [KitStatus.ACTIVATED]: 7,
  [KitStatus.UNACTIVATED]: 7,
  [KitStatus.KIT_RECEIVED]: 4,
  [KitStatus.SENT_OUT]: 2,
  [KitStatus.DNA_EXTRACTED]: 7,
  [KitStatus.SEQUENCING]: 16,
  [KitStatus.COMPLETED]: 2,
};

const allowedStatus = [
  KitStatus.UNACTIVATED,
  KitStatus.KIT_RECEIVED,
  KitStatus.SENT_OUT,
  KitStatus.DNA_EXTRACTED,
  KitStatus.SEQUENCING,
  KitStatus.COMPLETED,
];

export const kitStatusCounter = (
  kit: KitEntity,
  allowedDaysConfig: Record<string, number> = {},
  shouldCalculateByActivatedStatus = false,
) => {
  const formattedAllowedDays: Record<string, number> = isEmpty(
    allowedDaysConfig,
  )
    ? kitStatusCounterAllowedDays
    : allowedDaysConfig;

  const { statusHistory: originStatusHistory = [] } = kit;

  const currentStatus = kit.currentStatus?.status;

  let cumulativeDelay = 0;
  let currentDelays: { [status: string]: number } = {};
  let spentDays: { [status: string]: { spent: number; allowed: number } } = {};

  const mappedUnactivatedStatusHistory = [
    {
      status: KitStatus.UNACTIVATED,
      createdAt: kit.createdAt,
      id: '',
      userId: '',
      kidId: kit.id,
    },
    ...originStatusHistory,
  ];

  const activatedStatusHistory = getStatusHistory(
    mappedUnactivatedStatusHistory,
    KitStatus.ACTIVATED,
  );

  const statusHistory = mappedUnactivatedStatusHistory.reduce(
    (acc, history) => {
      // we will use activatedStatusHistory.createdAt if shouldCalculateByActivatedStatus is true
      // for UNACTIVATED status cause timeline using UNACTIVATED status with dynamic label
      if (history.status === KitStatus.UNACTIVATED) {
        return [
          ...acc,
          {
            ...history,
            createdAt:
              (shouldCalculateByActivatedStatus
                ? activatedStatusHistory?.createdAt
                : history.createdAt) ?? kit.createdAt,
          },
        ];
      }

      return [...acc, history];
    },
    [] as KitStatusHistoryEntity[],
  );

  if (!currentStatus) {
    return { cumulativeDelay: 0, currentDelays };
  }

  const workflow =
    TIMELINE_BASED_ON_WORKFLOW[
      (kit.channel?.timeline as keyof typeof TIMELINE_BASED_ON_WORKFLOW) ||
        WorkflowTimeline.AMD
    ];

  statusHistory.forEach(statusItem => {
    if (!allowedStatus.includes(statusItem.status)) return;

    const nextStatusItem = statusHistory.find(item =>
      workflow[statusItem.status]?.includes(item.status),
    );

    const dayDiff = dayjs(nextStatusItem?.createdAt)
      .startOf('day')
      .diff(dayjs(statusItem.createdAt).startOf('day'), 'd');

    const allowedDays = Number(formattedAllowedDays[statusItem.status]) || 0;
    const delayDays = dayDiff - allowedDays;

    if (statusItem.status !== KitStatus.UNACTIVATED) {
      cumulativeDelay += delayDays;
    }
    currentDelays[statusItem.status] = Math.max(delayDays, 0);
    spentDays[statusItem.status] = { spent: dayDiff, allowed: allowedDays };
  });

  return { cumulativeDelay, currentDelays, spentDays };
};

export const filterColumnTableByCondition = (
  columns: CustomColumnType<any>[],
  field: string,
) => {
  return columns.filter((column: any) => column[field]);
};

export const handleAwsError = ({ name }: { name: string }) => {
  let message = `error.REFRESH_AND_RETRY`;

  if (name === 'LimitExceededException') {
    message = `error.ATTEMPT_LIMIT_EXCEEDED`;
  }

  if (name === 'InvalidPasswordException') {
    message = `error.PASSWORD_PREVIOUSLY_USED`;
  }

  return notification.error({ message: i18next.t(message) });
};

export const translatedTextFunc = <T extends string>(
  translateValue: T,
  translatePath: string,
) => {
  const { t, exists } = i18next;
  const translateKey = `${translatePath}.${translateValue}`;
  return exists(translateKey) ? t(translateKey) : translateValue;
};

// TODO: Should be remove duplicate helper; an existing util already covers this behavior
export const generateSelectOptions = (
  data: { id: string; name: string }[],
  { labelField, valueField }: { labelField?: string; valueField?: string } = {},
) => {
  return data.map(item => ({
    value: item[valueField as keyof typeof item] ?? item.id,
    label: item[labelField as keyof typeof item] ?? item.name ?? '',
  }));
};

export const genRandomString = (length = 8) => {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }
  return result;
};

export { convertStringArrayToEnumLikeObject } from './enum-helpers';

const currencySignMap: Record<Currency | string, string> = {
  [Currency.SGD]: 'S$',
  [Currency.THB]: '฿',
  [Currency.MYR]: 'RM',
  [Currency.PHP]: '₱',
  [Currency.IDR]: 'Rp',
  [Currency.VND]: '₫',
  [Currency.SAR]: 'SAR ',
  [Currency.HKD]: 'HK$',
};

export const getCurrencySign = (currency?: Currency | string | null): string =>
  currencySignMap[currency || ''] || 'S$';
